import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import BottomNav from "@/components/BottomNav";
import PaymentTestModeBanner from "@/components/PaymentTestModeBanner";
// PERF: AgeGate, OnboardingFlow, CookieBanner all use framer-motion.
// Lazy-loading them keeps motion-dom (~25KB) out of the initial chunk.
const AgeGate = lazy(() => import("@/components/AgeGate"));
const CookieBanner = lazy(() => import("@/components/CookieBanner"));
const OnboardingFlow = lazy(() => import("@/components/OnboardingFlow"));
import { useLiveBuyPulse } from "@/hooks/useLiveBuyPulse";

function LiveBuyPulseRunner() {
  useLiveBuyPulse();
  return null;
}

function LiveBuyPulseMount() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const idle = (window as any).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500));
    const handle = idle(() => setReady(true));
    return () => {
      const cancel = (window as any).cancelIdleCallback;
      if (cancel && typeof handle === "number") cancel(handle);
    };
  }, []);
  if (!ready) return null;
  return <LiveBuyPulseRunner />;
}

const Index = lazy(() => import("./pages/Index"));
const Feed = lazy(() => import("./pages/Feed"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const Alerts = lazy(() => import("./pages/Alerts"));
const History = lazy(() => import("./pages/History"));
const Profile = lazy(() => import("./pages/Profile"));
const Auth = lazy(() => import("./pages/Auth"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const CheckoutTest = lazy(() => import("./pages/CheckoutTest"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Refunds = lazy(() => import("./pages/Refunds"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SharedDecision = lazy(() => import("./pages/SharedDecision"));
const TrackRecord = lazy(() => import("./pages/TrackRecord"));
const Methodology = lazy(() => import("./pages/Methodology"));
const About = lazy(() => import("./pages/About"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const EdgeDrop = lazy(() => import("./pages/EdgeDrop"));
const Receipt = lazy(() => import("./pages/Receipt"));
const BrandPreview = lazy(() => import("./pages/BrandPreview"));
const PredictionMarket = lazy(() => import("./pages/PredictionMarket"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PaymentTestModeBanner />
          <LiveBuyPulseMount />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/feed" element={<Feed />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/watchlist" element={<Watchlist />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/history" element={<History />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="/checkout/test" element={<CheckoutTest />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refunds" element={<Refunds />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/v/:decisionId" element={<SharedDecision />} />
              <Route path="/track-record" element={<TrackRecord />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/about" element={<About />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/edge-drop" element={<EdgeDrop />} />
              <Route path="/receipt/:id" element={<Receipt />} />
              <Route path="/brand/preview" element={<BrandPreview />} />
              <Route path="/prediction/:slug" element={<PredictionMarket />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <BottomNav />
          <Suspense fallback={null}>
            <OnboardingFlow />
            <AgeGate />
            <CookieBanner />
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
